// store/imageAnalysisStore.ts

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// AI 분석 결과 타입 정의
interface PredictionResult {
  rank: number;
  class_name: string;
  class_id: number;
  probability: number;
  
}

interface AiAnalysisResult {
  success: boolean;
  segmentation_applied: boolean;
    clothing_classification: {
    predictions: PredictionResult[];
  };
  color_classification : {
    id: number;
    is_multi: boolean;
  }
    image_path: string;
  pattern_classification: {
    predictions: PredictionResult[];
  };

}

// 스토어 상태 타입 정의
interface ImageAnalysisState {
  // 업로드된 이미지 URL
  uploadedImageUrl: string | null;
  
  // AI 분석 결과
  aiAnalysisResult: AiAnalysisResult | null;
  
  // 선택된 최상위 예측 결과 (편의를 위해)
  selectedPrediction: PredictionResult | null;
  
  // 분석 완료 날짜
  analysisCompletedAt: string | null;
  suggestedColors: Array<{ color_id: number; name_ko: string; name_en?: string; hex_code: string }>;

  // 액션 함수들
  setUploadedImageUrl: (url: string) => void;
  setAiAnalysisResult: (result: AiAnalysisResult, completedAt?: string) => void; 
  setColorSuggestions: (colors: ImageAnalysisState["suggestedColors"]) => void;
  clearData: () => void;
  
  // 편의 함수들
  getTopPrediction: () => PredictionResult | null;
  getPredictionByRank: (rank: number) => PredictionResult | null;
}

// Zustand 스토어 생성
export const useImageAnalysisStore = create<ImageAnalysisState>()(
  persist(
    (set, get) => ({  // 초기 상태
  uploadedImageUrl: null,
  aiAnalysisResult: null,
  selectedPrediction: null,
  analysisCompletedAt: null, 
  suggestedColors: [],
  

  // 업로드된 이미지 URL 설정
  setUploadedImageUrl: (url: string) => {
    set({ uploadedImageUrl: url });
  },

  // AI 분석 결과 설정
  setAiAnalysisResult: (result: AiAnalysisResult, completedAt?: string) => {
    const predictions = result?.clothing_classification?.predictions;
    set({
      aiAnalysisResult: result,
      selectedPrediction: predictions && predictions.length > 0 ? predictions[0] : null,
      analysisCompletedAt: completedAt || new Date().toISOString(),

    });
  },
  setColorSuggestions: (suggestedColors) => set({ suggestedColors }),

  // 모든 데이터 초기화
  clearData: () => {
    set({
      uploadedImageUrl: null,
      aiAnalysisResult: null,
      selectedPrediction: null,
      analysisCompletedAt: null,
      suggestedColors: [],
    });
  },

  // 최상위 예측 결과 가져오기 (rank 1)
  getTopPrediction: () => {
    const { aiAnalysisResult } = get();
    const predictions = aiAnalysisResult?.clothing_classification?.predictions;
    if (!predictions || !predictions.length) {
      return null;
    }
    return predictions.find(pred => pred.rank === 1) || predictions[0];
  },

  // 특정 순위의 예측 결과 가져오기
  getPredictionByRank: (rank: number) => {
    const { aiAnalysisResult } = get();
    const predictions = aiAnalysisResult?.clothing_classification?.predictions;
    if (!predictions || !predictions.length) {
      return null;
    }
    return predictions.find(pred => pred.rank === rank) || null;
  },
}),
{
  name: 'image-analysis-store', // localStorage 키 이름
  // sessionStorage 사용하려면:
  // storage: createJSONStorage(() => sessionStorage),
}
)
);

// 타입 export
export type { PredictionResult, AiAnalysisResult };
